import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Zap, Play, Pause, SkipForward, Square, Trash2, Plus,
  Mail, ClipboardList, Bell, ChevronRight, Clock, CheckCircle2, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const stepIcons = { send_email: Mail, create_task: ClipboardList, notification: Bell };
const stepColors = {
  send_email: 'text-blue-600 bg-blue-50 border-blue-200',
  create_task: 'text-amber-600 bg-amber-50 border-amber-200',
  notification: 'text-purple-600 bg-purple-50 border-purple-200',
};
const statusStyles = {
  active: { color: 'bg-emerald-500', label: 'Active', textColor: 'text-emerald-700' },
  paused: { color: 'bg-amber-500', label: 'Paused', textColor: 'text-amber-700' },
  completed: { color: 'bg-blue-500', label: 'Completed', textColor: 'text-blue-700' },
  stopped: { color: 'bg-red-500', label: 'Stopped', textColor: 'text-red-700' },
};

function formatDelay(step) {
  if (!step) return '';
  if (step.delay_type === 'immediate') return 'Immediately';
  if (step.delay_value === 1) return `${step.delay_type === 'hours' ? '1 hour' : '1 day'}`;
  return `${step.delay_value} ${step.delay_type}`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function DripAssignmentCard({ targetType, targetId }) {
  const [assignments, setAssignments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await API.get('/drip-assignments', { params: { target_type: targetType, target_id: targetId } });
      setAssignments(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [targetType, targetId]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await API.get('/drip-campaigns');
      setCampaigns(res.data.filter(c => c.status === 'active' || c.status === 'draft'));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const handleAssign = async () => {
    if (!selectedCampaignId) { toast.error('Select a campaign'); return; }
    setAssigning(true);
    try {
      await API.post('/drip-assignments', {
        campaign_id: selectedCampaignId,
        target_type: targetType,
        target_id: targetId,
        start_immediately: true,
      });
      toast.success('Campaign assigned and started');
      setShowAssignModal(false);
      setSelectedCampaignId('');
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally { setAssigning(false); }
  };

  const handlePause = async (id) => {
    try {
      await API.put(`/drip-assignments/${id}/pause`);
      toast.success('Campaign paused');
      fetchAssignments();
    } catch { toast.error('Failed to pause'); }
  };

  const handleResume = async (id) => {
    try {
      await API.put(`/drip-assignments/${id}/resume`);
      toast.success('Campaign resumed');
      fetchAssignments();
    } catch { toast.error('Failed to resume'); }
  };

  const handleSkip = async (id) => {
    try {
      await API.put(`/drip-assignments/${id}/skip`);
      toast.success('Step skipped');
      fetchAssignments();
    } catch { toast.error('Failed to skip'); }
  };

  const handleStop = async (id) => {
    try {
      await API.put(`/drip-assignments/${id}/stop`);
      toast.success('Campaign stopped');
      fetchAssignments();
    } catch { toast.error('Failed to stop'); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/drip-assignments/${id}`);
      toast.success('Assignment removed');
      fetchAssignments();
    } catch { toast.error('Failed to delete'); }
  };

  const activeAssignments = assignments.filter(a => a.status === 'active' || a.status === 'paused');
  const historyAssignments = assignments.filter(a => a.status === 'completed' || a.status === 'stopped');

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="drip-assignment-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#0073ea]" strokeWidth={1.5} />
            <h3 className="text-base font-semibold text-gray-900">Drip Campaigns</h3>
            {activeAssignments.length > 0 && (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                {activeAssignments.length} active
              </Badge>
            )}
          </div>
          <Button
            data-testid="assign-drip-btn"
            onClick={() => { fetchCampaigns(); setShowAssignModal(true); }}
            size="sm"
            className="bg-[#0073ea] hover:bg-[#0060c2] text-white h-8"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Assign Campaign
          </Button>
        </div>

        {loading ? (
          <div className="h-16 rounded-lg bg-gray-100 animate-pulse" />
        ) : assignments.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No drip campaigns assigned yet</p>
        ) : (
          <div className="space-y-3">
            {/* Active Campaigns */}
            {activeAssignments.map(a => {
              const st = statusStyles[a.status];
              const StepIcon = a.current_step ? (stepIcons[a.current_step.action_type] || Zap) : Zap;
              const completedCount = a.completed_steps?.length || 0;

              return (
                <div
                  key={a.id}
                  data-testid={`assignment-${a.id}`}
                  className="border border-gray-200 rounded-lg p-3 space-y-2"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${st.color}`} />
                      <span className="text-sm font-semibold text-gray-900">{a.campaign_name}</span>
                      <Badge variant="outline" className={`text-xs ${st.textColor} border-current/20`}>{st.label}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      {a.status === 'active' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handlePause(a.id)} data-testid={`pause-assignment-${a.id}`} className="h-7 px-1.5 text-amber-600 hover:bg-amber-50" title="Pause">
                            <Pause className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleSkip(a.id)} data-testid={`skip-step-${a.id}`} className="h-7 px-1.5 text-gray-500 hover:bg-gray-100" title="Skip Step">
                            <SkipForward className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {a.status === 'paused' && (
                        <Button variant="ghost" size="sm" onClick={() => handleResume(a.id)} data-testid={`resume-assignment-${a.id}`} className="h-7 px-1.5 text-emerald-600 hover:bg-emerald-50" title="Resume">
                          <Play className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleStop(a.id)} data-testid={`stop-assignment-${a.id}`} className="h-7 px-1.5 text-red-500 hover:bg-red-50" title="Stop">
                        <Square className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#0073ea] rounded-full transition-all"
                        style={{ width: `${a.total_steps ? (completedCount / a.total_steps) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {completedCount}/{a.total_steps} steps
                    </span>
                  </div>

                  {/* Current Step */}
                  {a.current_step && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500">Next:</span>
                      <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded border ${stepColors[a.current_step.action_type] || ''}`}>
                        <StepIcon className="h-3 w-3" />
                        <span>
                          {a.current_step.action_type === 'send_email' && (a.current_step.email_subject || 'Send Email')}
                          {a.current_step.action_type === 'create_task' && (a.current_step.task_title || 'Create Task')}
                          {a.current_step.action_type === 'notification' && 'Notification'}
                        </span>
                      </div>
                      {a.next_action_at && (
                        <span className="text-gray-400 flex items-center gap-0.5">
                          <Clock className="h-3 w-3" /> {formatDate(a.next_action_at)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Step Timeline (compact) */}
                  {a.steps && a.steps.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {a.steps.map((step, i) => {
                        const Icon = stepIcons[step.action_type] || Zap;
                        const isCompleted = i < (a.current_step_index || 0);
                        const isCurrent = i === (a.current_step_index || 0);
                        const completedStep = a.completed_steps?.find(cs => cs.step_index === i);
                        const wasSkipped = completedStep?.result === 'skipped';
                        return (
                          <div key={step.id || i} className="flex items-center gap-1">
                            {i > 0 && <ChevronRight className="h-3 w-3 text-gray-200" />}
                            <div
                              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs border ${
                                isCompleted
                                  ? wasSkipped ? 'bg-gray-100 text-gray-400 border-gray-200 line-through' : 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                  : isCurrent
                                    ? 'bg-blue-50 text-blue-700 border-blue-300 ring-1 ring-blue-200'
                                    : 'bg-gray-50 text-gray-400 border-gray-200'
                              }`}
                              title={`Step ${i+1}: ${step.action_type} — ${formatDelay(step)}`}
                            >
                              {isCompleted && !wasSkipped && <CheckCircle2 className="h-3 w-3" />}
                              {wasSkipped && <XCircle className="h-3 w-3" />}
                              {!isCompleted && <Icon className="h-3 w-3" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Campaign History */}
            {historyAssignments.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2">History</p>
                {historyAssignments.map(a => {
                  const st = statusStyles[a.status];
                  return (
                    <div
                      key={a.id}
                      data-testid={`assignment-history-${a.id}`}
                      className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        {a.status === 'completed' ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />
                        ) : (
                          <AlertCircle className="h-3.5 w-3.5 text-red-400" />
                        )}
                        <span className="text-sm text-gray-600">{a.campaign_name}</span>
                        <span className="text-xs text-gray-400">
                          {a.completed_steps?.length || 0}/{a.total_steps} steps
                        </span>
                        {a.stopped_reason && a.stopped_reason !== 'completed' && (
                          <span className="text-xs text-red-400">({a.stopped_reason})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{formatDate(a.updated_at)}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="h-6 px-1 text-gray-300 hover:text-red-500">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Assign Campaign Modal */}
        <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm" data-testid="assign-campaign-modal">
            <DialogHeader>
              <DialogTitle>Assign Drip Campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {campaigns.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No campaigns available. Create one in Drip Campaigns.</p>
              ) : (
                <>
                  <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                    <SelectTrigger data-testid="select-campaign-dropdown" className="bg-white border-gray-300 text-gray-900">
                      <SelectValue placeholder="Select a campaign..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {campaigns.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-gray-900">
                          <div className="flex items-center gap-2">
                            <span>{c.name}</span>
                            <span className="text-xs text-gray-400">({c.steps?.length || 0} steps)</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCampaignId && (() => {
                    const c = campaigns.find(x => x.id === selectedCampaignId);
                    if (!c) return null;
                    return (
                      <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
                        <p className="font-medium text-gray-900">{c.name}</p>
                        <p>{c.steps?.length || 0} steps &middot; {c.campaign_type} &middot; {c.business_hours_only ? 'Business hours' : 'Anytime'}</p>
                        {c.stop_on_statuses?.length > 0 && <p>Stops on: {c.stop_on_statuses.join(', ')}</p>}
                      </div>
                    );
                  })()}
                </>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowAssignModal(false)} className="text-gray-500">Cancel</Button>
                <Button
                  data-testid="confirm-assign-btn"
                  onClick={handleAssign}
                  disabled={!selectedCampaignId || assigning}
                  className="bg-[#0073ea] hover:bg-[#0060c2] text-white"
                >
                  {assigning ? 'Assigning...' : 'Start Campaign'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
